import React, { useState, useEffect } from 'react';
import KitchenRegistration from '../components/RegistrationForms/KitchenRegistration';
import { useNavigate } from 'react-router-dom';
import SigninModal from '../modal/SigninModal';
import Otp from '../modal/Otp';
import { kitchenService } from '../services';
import { toast } from 'react-toastify';

const KitchenRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [showSigninModal, setShowSigninModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('kitchenAuthToken');
    
    if (!token) {
      // No token, show signin modal
      setLoading(false);
      setShowSigninModal(true);
      return;
    }

    // Token exists, check restaurant details
    try {
      const response: any = await kitchenService.getRestaurantDetails();
      
      if (response?.status && response?.data) {
        const step = response.data.step || 0;
        
        if (step >= 8) {
          // Restaurant is fully registered, go to home
          localStorage.setItem('kitchenRegistered', 'true');
          navigate('/', { replace: true });
        } else {
          // Incomplete registration, show forms
          setLoading(false);
          setShowRegistrationForm(true);
        }
      } else {
        // No restaurant data, show forms
        setLoading(false);
        setShowRegistrationForm(true);
      }
    } catch (error) {
      console.error('Error checking restaurant details:', error);
      // On error, show forms (user is authenticated but no restaurant data)
      setLoading(false);
      setShowRegistrationForm(true);
    }
  };

  const handleGetStarted = async () => {
    if (!mobileNumber || mobileNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      // Send OTP
      const phoneNumber = `+91${mobileNumber}`;
      const response: any = await kitchenService.sendOtp(phoneNumber);
      
      if (response?.status && response?.data?.message) {
        // Auto-fill the OTP
        setOtp(response.data.message);
        setShowSigninModal(false);
        setShowOtpModal(true);
        toast.success('OTP sent successfully!');
      } else {
        toast.error('Failed to send OTP');
      }
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      toast.error(error?.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleVerifyOtp = async (otpValue: string) => {
    try {
      const phoneNumber = `+91${mobileNumber}`;
      const response: any = await kitchenService.verifyOtp(phoneNumber, otpValue);
      
      if (response?.status && response?.data?.token) {
        // Save token and phone number
        localStorage.setItem('kitchenAuthToken', response.data.token);
        localStorage.setItem('verifiedPhoneNumber', phoneNumber);
        setShowOtpModal(false);
        toast.success('OTP verified successfully!');
        
        // Check if restaurant exists
        try {
          const detailsResponse: any = await kitchenService.getRestaurantDetails();
          
          if (detailsResponse?.status && detailsResponse?.data) {
            const step = detailsResponse.data.step || 0;
            
            if (step >= 8) {
              // Restaurant already registered, go to home
              localStorage.setItem('kitchenRegistered', 'true');
              navigate('/', { replace: true });
            } else {
              // Incomplete registration, show forms
              setShowRegistrationForm(true);
            }
          } else {
            // New restaurant, show forms
            setShowRegistrationForm(true);
          }
        } catch (detailsError) {
          // No restaurant data, show forms for new registration
          setShowRegistrationForm(true);
        }
      } else {
        toast.error('Invalid OTP');
      }
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      toast.error(error?.message || 'Failed to verify OTP. Please try again.');
    }
  };

  const handleComplete = () => {
    // Registration completed successfully
    localStorage.setItem('kitchenRegistered', 'true');
    toast.success('Registration completed successfully!');
    
    // Force a full page reload to ensure ProtectedRoute picks up the new status
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-Urbanist">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showRegistrationForm && (
        <KitchenRegistration onComplete={handleComplete} />
      )}
      
      <SigninModal
        isOpen={showSigninModal}
        onClose={() => {}}
        onGetStarted={handleGetStarted}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
      />
      
      <Otp
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        mobileNumber={`+91${mobileNumber}`}
        onVerify={handleVerifyOtp}
        autoFillOtp={otp}
      />
    </>
  );
};

export default KitchenRegistrationPage;
